// The wire payload for POST /score_specs and PATCH /score_specs/:id.
//
// The editor buffer and the payload are deliberately NOT the same object. GET echoes `spec_json`
// verbatim, so a row authored before Aug 27 still carries keys the format has since dropped — and
// stripping them out of the BUFFER on load would be a write the operator did not make, flipping
// `isDirty` on a spec they only opened to read (the same reason UnknownRootKeysStrip removes
// nothing automatically). So the buffer keeps what the server sent, and this builds what the server
// now accepts.
//
// Only mechanical removals belong here. Anything that needs a decision — which half of the output
// contract to keep when a legacy row declares both — is the operator's, and is surfaced as a
// problem row with a resolve control instead of being silently picked for them.
//
//   * `enabled_in` and `score_gen.parallelize` were removed from the format (PR #455, Changes 4/5).
//     Both compile paths strip them at load rather than rejecting them, so sending them is not an
//     error — it is just noise that reads as a live setting on the next GET.
//   * A blank `format_prompt` already counts as absent at the API's XOR check, so dropping it is
//     not what keeps a structured-output spec from 422ing. It is dropped anyway so the payload says
//     what it means: an empty prose field is an undeclared prose contract, not a declared empty one.

const DEAD_TOP_LEVEL_KEYS = ['enabled_in'];
const DEAD_SCORE_GEN_KEYS = ['parallelize'];

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isBlank = (v) => typeof v === 'string' && v.trim() === '';

// Key order is preserved on the way through. It has no meaning on the wire, but the response echo
// replaces the buffer, and `isDirty` is a key-order-sensitive JSON.stringify comparison — a payload
// that reorders keys would come back as a spurious diff.
export const toPayload = (definition) => {
  if (!isObject(definition)) return definition;

  const payload = {};
  Object.keys(definition).forEach((key) => {
    if (DEAD_TOP_LEVEL_KEYS.indexOf(key) > -1) return;
    payload[key] = definition[key];
  });

  const gen = payload.score_gen;
  if (isObject(gen)) {
    const nextGen = {};
    Object.keys(gen).forEach((key) => {
      if (DEAD_SCORE_GEN_KEYS.indexOf(key) > -1) return;
      if (key === 'format_prompt' && isBlank(gen[key])) return;
      nextGen[key] = gen[key];
    });
    payload.score_gen = nextGen;
  }

  return payload;
};

// What `toPayload` would drop, for the strip that tells the operator a saved row is about to lose
// keys it can still see. Paths, not values — the values are already on screen.
export const droppedPaths = (definition) => {
  if (!isObject(definition)) return [];
  const paths = DEAD_TOP_LEVEL_KEYS.filter((key) => definition[key] !== undefined);
  const gen = isObject(definition.score_gen) ? definition.score_gen : {};
  return paths.concat(DEAD_SCORE_GEN_KEYS.filter((key) => gen[key] !== undefined).map((key) => `score_gen.${key}`));
};
